/**
 * Tips & tricks: what Nexus Open can do, in short. `keys` are shown as keyboard
 * shortcuts; text between `backticks` is shown as something to type.
 */
export interface Tip {
  text: string;
  keys?: string;
}
export interface TipSection {
  id: string;
  title: string;
  tips: Tip[];
}

const nl: TipSection[] = [
  {
    id: 'capture',
    title: 'Snel iets vastleggen',
    tips: [
      {
        text: 'Typ bovenaan het dashboard wat je wilt doen. Enter maakt een taak in je Inbox, de punaise bewaart het in Onthouden.',
      },
      { text: 'Van overal: snel een taak toevoegen.', keys: 'Ctrl+Shift+Space' },
      {
        text: 'Een datum met `@morgen`, `@vrijdag 14u`, `@12/10` of `@over 3 dagen`; prioriteit met `!!`; een tag met `#klant`.',
      },
      {
        text: 'In Onthouden geeft `@morgen 9u` een herinnering; zonder datum blijft het gewoon bewaard.',
      },
    ],
  },
  {
    id: 'tasks',
    title: 'Taken',
    tips: [
      {
        text: 'Typ `[ ] ` aan het begin van een regel voor een taak, op elke pagina. Ze verschijnt ook in Taken en op het dashboard.',
      },
      { text: 'Enter na een taak maakt een nieuwe, Tab maakt er een subtaak van.' },
      {
        text: 'Typ `/taakpagina` in een taakregel om er een eigen pagina met eigenschappen van te maken.',
      },
      { text: '`/deadline` en `/herinnering` zetten een datum of herinnering in de regel.' },
      {
        text: 'Afgevinkte taken van een pagina zet je in één klik in het Archief (knop bovenaan de pagina).',
      },
      {
        text: 'In de kalender kies je dag, week of maand, en sleep je een taak naar een andere dag of een ander uur.',
      },
    ],
  },
  {
    id: 'writing',
    title: 'Schrijven en opmaak',
    tips: [
      {
        text: 'Typ `/` voor alles wat je kunt invoegen: koppen, tabellen, kolommen, inklapblokken, afbeeldingen, sjablonen.',
      },
      { text: 'Selecteer tekst voor de opmaakbalk: AI, vet, kleur, markeren, uitlijnen, link.' },
      {
        text: '`[[` linkt naar een andere pagina, `![[` toont een pagina of bestand op deze pagina, `#` maakt een tag.',
      },
      {
        text: 'Plak of sleep een afbeelding rechtstreeks in de pagina; versleep de hoek om ze kleiner te maken.',
      },
      {
        text: 'Markdown werkt tijdens het typen: `# ` kop, `- ` opsomming, `> ` citaat, `**vet**`, `==markeren==`, ``` code.',
      },
      {
        text: 'De greep links van een blok verplaatst het; klik erop voor omzetten, dupliceren of verwijderen.',
      },
    ],
  },
  {
    id: 'navigate',
    title: 'Vinden en navigeren',
    tips: [
      { text: 'Zoeken of een opdracht uitvoeren.', keys: 'Ctrl+K' },
      { text: 'Snel naar een pagina.', keys: 'Ctrl+O' },
      { text: 'Dagnotitie van vandaag.', keys: 'Ctrl+Shift+D' },
      { text: 'Ctrl-klik opent iets in een nieuw tabblad; middelklik op een tabblad sluit het.' },
      {
        text: 'Rechtsklik (of lang drukken op de telefoon) op een pagina in de zijbalk: favoriet, verplaatsen, subpagina, exporteren…',
      },
      {
        text: 'Het vaste menu stel je zelf samen: knop naast "Nexus Open" of rechtsklik op een item.',
      },
    ],
  },
  {
    id: 'databases',
    title: 'Databases',
    tips: [
      { text: 'Een rij openen toont de pagina in een pop-up; Ctrl-klik opent ze als pagina.' },
      {
        text: 'Elke database heeft weergaven: tabel, bord, lijst, kalender en galerij, met eigen filters en sortering.',
      },
      { text: 'Een gewone pagina maak je een database met de knop "Database" bovenaan.' },
    ],
  },
  {
    id: 'ai',
    title: 'AI-assistent',
    tips: [
      { text: 'Open de assistent.', keys: 'Ctrl+J' },
      {
        text: 'Selecteer tekst en kies AI in de opmaakbalk om te herschrijven, samen te vatten of te vertalen.',
      },
      {
        text: '"Taken uit tekst" haalt de taken uit een verslag. Wijzigingen van de AI vragen altijd eerst je bevestiging.',
      },
      { text: '"Verborgen voor AI" houdt een pagina (en wat eronder staat) buiten de assistent.' },
    ],
  },
  {
    id: 'more',
    title: 'Nog handig',
    tips: [
      {
        text: 'Exporteer een pagina als Markdown, webpagina, PDF of Word via "Exporteren" bovenaan de pagina.',
      },
      { text: 'Klik op Voortgang op het dashboard om te zien wat je vandaag al deed.' },
      { text: 'Eigen iconen: klik op het icoon van een pagina en kies "Eigen afbeelding…".' },
      { text: 'Op de telefoon: de ronde + rechtsonder voegt snel een taak toe.' },
    ],
  },
];

const en: TipSection[] = [
  {
    id: 'capture',
    title: 'Capture quickly',
    tips: [
      {
        text: 'Type what you want to do at the top of the dashboard. Enter makes a task in your Inbox, the pin keeps it in Remember.',
      },
      { text: 'From anywhere: add a task quickly.', keys: 'Ctrl+Shift+Space' },
      {
        text: 'A date with `@tomorrow`, `@friday 2pm` or `@10/12`; priority with `!!`; a tag with `#client`.',
      },
    ],
  },
  {
    id: 'tasks',
    title: 'Tasks',
    tips: [
      { text: 'Type `[ ] ` at the start of a line for a task, on any page.' },
      { text: 'Type `/taakpagina` in a task line to turn it into a page of its own.' },
      {
        text: 'Archive the checked tasks of a page with one click (button at the top of the page).',
      },
      {
        text: 'In the calendar, choose day, week or month and drag a task to another day or hour.',
      },
    ],
  },
  {
    id: 'writing',
    title: 'Writing and formatting',
    tips: [
      { text: 'Type `/` for everything you can insert.' },
      { text: 'Select text for the formatting bar: AI, bold, colour, highlight, alignment, link.' },
      { text: '`[[` links a page, `![[` embeds a page or file, `#` makes a tag.' },
      { text: 'Paste or drop an image straight into the page.' },
    ],
  },
  {
    id: 'navigate',
    title: 'Finding your way',
    tips: [
      { text: 'Search or run a command.', keys: 'Ctrl+K' },
      { text: 'Jump to a page.', keys: 'Ctrl+O' },
      { text: "Today's daily note.", keys: 'Ctrl+Shift+D' },
      {
        text: 'Right-click a page in the sidebar for its menu; customise the fixed menu next to "Nexus Open".',
      },
    ],
  },
  {
    id: 'more',
    title: 'Also handy',
    tips: [
      { text: 'Export a page as Markdown, web page, PDF or Word via "Export".' },
      { text: 'Open the assistant with Ctrl+J; "Hidden from AI" keeps a page out of it.' },
    ],
  },
];

export const tipsFor = (language: string) => (language.startsWith('nl') ? nl : en);
